import React, { type ReactElement } from 'react';

export type DatabaseViewRenderComponent = 'rows' | 'row' | 'cell' | 'value';

declare const __DATABASE_VIEW_REACT_PROFILING__: boolean;

/**
 * Build-time switch for the React profiling instrumentation.
 *
 * Vite replaces this value with a literal. Keeping the switch here lets the
 * normal application build remove profiler boundaries and per-cell recording
 * calls instead of paying their runtime cost when profiling is disabled.
 */
export const DATABASE_VIEW_REACT_PROFILING_BUILD_ENABLED =
  typeof __DATABASE_VIEW_REACT_PROFILING__ !== 'undefined' &&
  __DATABASE_VIEW_REACT_PROFILING__;

export type DatabaseViewReactProfilerRecord = {
  id: string;
  phase: 'mount' | 'update' | 'nested-update';
  actualDuration: number;
  baseDuration: number;
  startTime: number;
  commitTime: number;
};

export interface DatabaseViewRenderRecord {
  component: DatabaseViewRenderComponent;
  entryId?: string;
  fieldId?: string;
  startTime: number;
}

export function profileDatabaseView(
  id: string,
  content: ReactElement,
  key?: React.Key
): ReactElement {
  return DATABASE_VIEW_REACT_PROFILING_BUILD_ENABLED &&
    isDatabaseViewReactProfilingEnabled() ? (
    <React.Profiler key={key} id={id} onRender={recordDatabaseViewReactProfile}>
      {content}
    </React.Profiler>
  ) : (
    content
  );
}

export function recordDatabaseViewRender(
  component: DatabaseViewRenderComponent,
  entryId?: string,
  fieldId?: string
): void {
  if (!DATABASE_VIEW_REACT_PROFILING_BUILD_ENABLED) {
    return;
  }
  const profilerWindow = globalThis as typeof globalThis & {
    __databaseViewRenderRecords?: DatabaseViewRenderRecord[];
  };
  const records = profilerWindow.__databaseViewRenderRecords;
  if (records === undefined) {
    return;
  }
  records.push({ component, entryId, fieldId, startTime: performance.now() });
}

function isDatabaseViewReactProfilingEnabled(): boolean {
  if (!DATABASE_VIEW_REACT_PROFILING_BUILD_ENABLED) {
    return false;
  }
  const profilerWindow = globalThis as typeof globalThis & {
    __databaseViewReactProfilerRecords?: DatabaseViewReactProfilerRecord[];
  };
  return profilerWindow.__databaseViewReactProfilerRecords !== undefined;
}

function recordDatabaseViewReactProfile(
  id: string,
  phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number,
  baseDuration: number,
  startTime: number,
  commitTime: number
): void {
  const profilerWindow = globalThis as typeof globalThis & {
    __databaseViewReactProfilerRecords?: DatabaseViewReactProfilerRecord[];
  };
  profilerWindow.__databaseViewReactProfilerRecords?.push({
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime
  });
}
